import React, { useState, useEffect, useRef } from 'react';

import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getLocalChapterGroups,
  updateLocalChapterGroup,
  createLocalChapterGroup,
} from '../../../endpoints/chaptersTs';
import { roles, groupTypes } from '../../../utils/variables';
import dayjs from 'dayjs';
import {
  LocalChapterGroup,
  LocalChapterCommitteePut,
  LocalChapterCommitteePost,
} from '../../../types/committee';

interface LCSocialProps {
  id: string;
  lcAccess: boolean;
}

const LCSocial: React.FC<LCSocialProps> = ({ id, lcAccess }) => {
  // Suppress ResizeObserver errors
  useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (
        e.message ===
        'ResizeObserver loop completed with undelivered notifications.'
      ) {
        e.stopImmediatePropagation();
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    return () => window.removeEventListener('error', handleResizeObserverError);
  }, []);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [filteredGroups, setFilteredGroups] = useState<LocalChapterGroup[]>([]);
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<LocalChapterCommitteePut>>(
    {}
  );
  const [isDataRefreshing, setIsDataRefreshing] = useState<boolean>(false);
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [createData, setCreateData] = useState<
    Partial<LocalChapterCommitteePost>
  >({
    name: '',
    description: '',
    group_type_id: '',
    is_active: true,
  });
  const scrollToGroupRef = useRef<string | null>(null);
  const savedScrollPositionRef = useRef<number>(0);

  const {
    data: groupsData,
    isFetching,
    refetch,
  } = useApiGet(['lc-groups', id], () => getLocalChapterGroups(id), {
    enabled: true,
    retry: 1,
  });

  const handleSearch = (searchValue: string) => {
    setSearch(searchValue);
  };

  useEffect(() => {
    if (groupsData) {
      setFilteredGroups(
        groupsData.filter((group) => {
          const matchesSearch = group.name
            .toLowerCase()
            .includes(search.toLowerCase());
          const matchesStatus =
            statusFilter === 'all' ||
            (statusFilter === 'active' && group.is_active) ||
            (statusFilter === 'inactive' && !group.is_active);
          return matchesSearch && matchesStatus;
        })
      );
    }
  }, [groupsData, search, statusFilter]);

  const { mutate: updateGroupDetails } = useApiSend(
    (data: { groupId: string } & LocalChapterCommitteePut) =>
      updateLocalChapterGroup(data.groupId, {
        name: data.name,
        description: data.description,
        group_type_id: data.group_type_id,
        is_active: data.is_active,
      }),
    (data) => {
      setIsSaving(false);
      setEditingGroup(null);
      setEditData({});
      setIsDataRefreshing(false);
      refetch();
    },
    (error: Error) => {
      setIsSaving(false);
      setIsDataRefreshing(false);
    }
  );

  const { mutate: createGroup } = useApiSend(
    (data: LocalChapterCommitteePost) => createLocalChapterGroup(id, data),
    (data) => {
      setIsSaving(false);
      setShowCreateDialog(false);
      setCreateData({
        name: '',
        description: '',
        group_type_id: '',
        is_active: true,
      });
      setIsDataRefreshing(false);
      refetch();
    },
    (error: Error) => {
      setIsSaving(false);
      setIsDataRefreshing(false);
    }
  );

  const canEdit = (): boolean => {
    if (lcAccess) {
      let userRole = localStorage.getItem('role');
      let rolesAllowed = [roles.Admin, roles.Support, roles.LCOfficer];
      return rolesAllowed.includes(userRole);
    }
    return false;
  };

  const handleEditGroup = (group: LocalChapterGroup) => {
    setEditingGroup(group.id);
    setEditData({
      name: group.name,
      description: group.description,
      group_type_id: group.group_type_id,
      is_active: group.is_active,
    });
    // Save to refs for persistence
    scrollToGroupRef.current = group.id;
    savedScrollPositionRef.current = window.scrollY;
  };

  const handleSaveGroup = () => {
    if (editingGroup && editData.name && editData.description) {
      setIsSaving(true);
      setIsDataRefreshing(true);
      updateGroupDetails({
        groupId: editingGroup,
        name: editData.name,
        description: editData.description,
        group_type_id: editData.group_type_id!,
        is_active: editData.is_active!,
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingGroup(null);
    setEditData({});
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
  };

  const handleCreateGroup = () => {
    setIsSaving(true);
    createGroup(createData as LocalChapterCommitteePost);
  };

  const handleCancelCreate = () => {
    setShowCreateDialog(false);
    setCreateData({
      name: '',
      description: '',
      group_type_id: '',
      is_active: true,
    });
  };

  // Scroll to group after data refresh
  useEffect(() => {
    const groupToScrollTo = scrollToGroupRef.current;
    const positionToRestore = savedScrollPositionRef.current;

    if (groupToScrollTo && groupsData && !isDataRefreshing) {
      // Use requestAnimationFrame to prevent ResizeObserver issues
      requestAnimationFrame(() => {
        setTimeout(() => {
          // First restore the saved scroll position
          window.scrollTo({
            top: positionToRestore,
            behavior: 'auto' as ScrollBehavior,
          });

          // Then scroll to the specific group with a slight delay
          requestAnimationFrame(() => {
            setTimeout(() => {
              const element = document.getElementById(
                `group-${groupToScrollTo}`
              );
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          });

          // Clean up refs
          scrollToGroupRef.current = null;
          savedScrollPositionRef.current = 0;
        }, 300);
      });
    }
  }, [groupsData, isDataRefreshing]);

  const formatLastMeetingDate = (dateString: string | null): string => {
    if (!dateString) return 'No meetings yet';

    try {
      // Parse MM-DD-YYYY format
      const [month, day, year] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return dayjs(date).format('MMM D, YYYY');
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!groupsData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid container spacing={2} sx={{ p: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={search}
            fullWidth={true}
            onChange={(event) => handleSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          {canEdit() && (
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateDialog(true)}
              disabled={isSaving}
              fullWidth
              size="small"
            >
              Create Group
            </Button>
          )}
        </Grid>
      </Grid>
      {filteredGroups.map((group) => (
        <Grid item xs={12} md={6} sx={{ p: 1 }} key={group.id}>
          <Card
            id={`group-${group.id}`}
            sx={{
              p: 2,
              boxShadow: 3,
              borderRadius: 2,
              minHeight: editingGroup === group.id ? 300 : 200,
              transition: 'min-height 0.2s ease-in-out',
            }}
          >
            <CardHeader
              title={
                editingGroup === group.id ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editData.name || ''}
                    onChange={(e) =>
                      setEditData({ ...editData, name: e.target.value })
                    }
                    variant="outlined"
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">{group.name}</Typography>
                    <Chip
                      label={group.is_active ? 'Active' : 'Inactive'}
                      color={group.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                )
              }
              subheader={
                editingGroup === group.id ? (
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={editData.description || ''}
                    onChange={(e) =>
                      setEditData({ ...editData, description: e.target.value })
                    }
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {group.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last Meeting:{' '}
                      {formatLastMeetingDate(group.last_meeting_date)}
                    </Typography>
                  </Box>
                )
              }
              action={
                canEdit() &&
                editingGroup !== group.id && (
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditGroup(group)}
                    disabled={isSaving}
                    color="info"
                  >
                    Edit
                  </Button>
                )
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {editingGroup === group.id && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={editData.group_type_id || group.group_type_id}
                        label="Type"
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            group_type_id: e.target.value,
                          })
                        }
                      >
                        <MenuItem value={groupTypes.Committee}>
                          Committee
                        </MenuItem>
                        <MenuItem value={groupTypes.Crafts}>Crafts</MenuItem>
                        <MenuItem value={groupTypes.Exercis}>Exercise</MenuItem>
                        <MenuItem value={groupTypes.Dining}>Dining</MenuItem>
                        <MenuItem value={groupTypes.Wine}>Wine</MenuItem>
                        <MenuItem value={groupTypes.Book}>Book</MenuItem>
                        <MenuItem value={groupTypes.Game}>Game</MenuItem>
                        <MenuItem value={groupTypes.Coffee}>Coffee</MenuItem>
                        <MenuItem value={groupTypes.Other}>Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={editData.is_active ? 'active' : 'inactive'}
                        label="Status"
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            is_active: e.target.value === 'active',
                          })
                        }
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveGroup}
                        disabled={
                          isSaving || !editData.name || !editData.description
                        }
                        color="success"
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        color="secondary"
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              )}
              {!editingGroup && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Type: {group.group_type}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* Create Group Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={handleCancelCreate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Social Group</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Group Name"
                value={createData.name}
                onChange={(e) =>
                  setCreateData({ ...createData, name: e.target.value })
                }
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={createData.description}
                onChange={(e) =>
                  setCreateData({ ...createData, description: e.target.value })
                }
                fullWidth
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Group Type</InputLabel>
                <Select
                  value={createData.group_type_id}
                  label="Group Type"
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      group_type_id: e.target.value,
                    })
                  }
                  required
                >
                  <MenuItem value={groupTypes.Crafts}>Crafts</MenuItem>
                  <MenuItem value={groupTypes.Exercis}>Exercise</MenuItem>
                  <MenuItem value={groupTypes.Dining}>Dining</MenuItem>
                  <MenuItem value={groupTypes.Wine}>Wine</MenuItem>
                  <MenuItem value={groupTypes.Book}>Book</MenuItem>
                  <MenuItem value={groupTypes.Game}>Game</MenuItem>
                  <MenuItem value={groupTypes.Coffee}>Coffee</MenuItem>
                  <MenuItem value={groupTypes.Other}>Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={createData.is_active ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      is_active: e.target.value === 'active',
                    })
                  }
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCreate} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateGroup}
            variant="contained"
            color="success"
            disabled={
              isSaving ||
              !createData.name ||
              !createData.description ||
              !createData.group_type_id
            }
          >
            Create Group
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default LCSocial;
